// A tiny request bus between the outbox's UI and the composers.
//
// "Edit" on a pending reply (or on the compose failure toast) has to put the
// message back into a composer that lives somewhere else in the tree. The
// composer for a key (a ticket id, or 'compose') registers a handler while it
// is mounted; whoever wants the message back calls `requestRestore`.
//
// Pure: no React, so it can be tested directly.

import type { OutboxJobView } from './send-outbox';

/** Return true when the message was taken back (the handler owns removing the
 *  job from the outbox), false to refuse — e.g. the person declined to replace
 *  what they had typed. */
export type RestoreHandler = (job: OutboxJobView) => boolean;

const handlers = new Map<string, Set<RestoreHandler>>();

/** Register `handler` for jobs with this `key`. Returns the unregister fn. */
export function onRestoreRequest(key: string, handler: RestoreHandler): () => void {
  let set = handlers.get(key);
  if (!set) {
    set = new Set();
    handlers.set(key, set);
  }
  set.add(handler);
  return () => {
    set?.delete(handler);
    if (set && set.size === 0) handlers.delete(key);
  };
}

/** Ask the composer for `job.key` to take the message back. False when no
 *  composer is mounted for that key or it declined. */
export function requestRestore(job: OutboxJobView): boolean {
  const set = handlers.get(job.key);
  if (!set) return false;
  for (const h of [...set]) {
    if (h(job)) return true;
  }
  return false;
}
